import React, { useState } from 'react';
import DestinationPicker from './DestinationPicker';
import {
  destinationErrorMessage,
  destinationLabel,
  destinationOf,
  setVendorInvoiceDestination,
  type VendorInvoiceDestination,
} from '../../lib/vendorInvoiceDestinationApi';
import type { VendorInvoice } from '../../lib/vendorInvoiceApi';

// The review-screen home of "where does this invoice go?" (D95). Editable until the
// invoice is confirmed (the server locks it then); afterwards, or in view mode, it is
// shown and not editable. A change saves at once; a refusal is shown in plain words.

export interface DestinationCardProps {
  invoice: Pick<VendorInvoice, '_id' | 'destination' | 'inventoryConfirmedAt'>;
  /** The page reloads the invoice after a save, so the card never holds a stale copy. */
  onSaved: (destination: VendorInvoiceDestination) => void | Promise<void>;
  readOnly?: boolean;
}

const DestinationCard: React.FC<DestinationCardProps> = ({ invoice, onSaved, readOnly = false }) => {
  const stored = destinationOf(invoice);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (invoice.inventoryConfirmedAt || readOnly) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="destination-locked">
        <span className="font-semibold text-slate-800">Destination: </span>
        {destinationLabel(stored)}
      </div>
    );
  }

  async function change(next: VendorInvoiceDestination) {
    if (next === stored) return;
    setBusy(true);
    setMessage(null);
    try {
      await setVendorInvoiceDestination(invoice._id, next);
      await onSaved(next);
    } catch (e) {
      setMessage(destinationErrorMessage(e, 'Could not change the destination.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-3 space-y-2" data-testid="destination-card">
      <label htmlFor={`destination-${invoice._id}`} className="block text-xs font-semibold text-slate-800">
        Destination
      </label>
      <DestinationPicker id={`destination-${invoice._id}`} value={stored} onChange={change} disabled={busy} />
      {stored === 'expense' ? (
        <div className="text-xs text-slate-600">Billed and paid like any invoice, but no stock, catalogue price or purchase-order match.</div>
      ) : null}
      {message ? (
        <div role="alert" className="text-xs text-rose-700 whitespace-pre-line">
          {message}
        </div>
      ) : null}
    </div>
  );
};

export default DestinationCard;
